import React, { useRef, useEffect, useState, useCallback } from 'react'

import { useNavigate } from 'react-router-dom'

import { Schedule } from '@/core/api/entities/schedule'
import { useAccountConfig } from '@/core/hooks/useAccountConfig'
import { useSchedules } from '@/core/hooks/useSchedule'
import { useTranslation } from '@/core/i18n/useTranslation'
import Icon from '@/shared/components/atoms/Icon'

import ScheduleCarousel, { ScheduleCarouselRef } from './ScheduleCarousel'
import ScheduleSkeleton from './ScheduleSkeleton'
import { FilteredSection } from '../hooks/useFilteredScheduleSections'

interface FilteredScheduleSectionProps {
	section: FilteredSection
	onScheduleClick: (schedule: Schedule) => void
	onFavoriteClick?: (schedule: Schedule, isFavoritesCarousel: boolean) => void
	onLoadingStateChange?: (isLoading: boolean) => void
	onAssign?: (scheduleId: number, name: string) => void
}

const FilteredScheduleSection: React.FC<FilteredScheduleSectionProps> = ({
	section,
	onScheduleClick,
	onFavoriteClick,
	onLoadingStateChange,
	onAssign,
}) => {
	const { t } = useTranslation('common')
	const navigate = useNavigate()
	const carouselRef = useRef<ScheduleCarouselRef>(null)
	const [currentPage, setCurrentPage] = useState(1)

	// Build query params based on filter type
	const queryParams = React.useMemo(() => {
		const params: Record<string, number | boolean> = {
			public: true,
			per_page: 5,
			page: currentPage,
		}

		if (section.type === 'content_category') {
			params.content_category_ids = section.filterValue
		}

		return params
	}, [section, currentPage])

	// Fetch schedules for this filter
	const { data: schedules, isLoading } = useSchedules({
		config: useAccountConfig({
			params: {
				...queryParams,
			},
		}),
	})

	// Notify parent about loading state changes
	useEffect(() => {
		if (onLoadingStateChange) {
			onLoadingStateChange(isLoading)
		}
	}, [isLoading, onLoadingStateChange])

	// Handle page change for pagination
	const handlePageChange = useCallback(
		(direction: 'next' | 'prev') => {
			if (
				!schedules?.meta?.pagination ||
				schedules?.meta?.pagination?.total_pages <= 1
			) {
				return
			}

			setCurrentPage((prev) => {
				if (direction === 'next') {
					if (prev >= schedules?.meta?.pagination?.total_pages) {
						return 1
					}
					return prev + 1
				} else {
					if (prev <= 1) {
						return schedules?.meta?.pagination?.total_pages
					}
					return prev - 1
				}
			})
		},
		[schedules]
	)

	const handleScrollStateChange = useCallback(() => {
		// Scroll state tracking for future use if needed
	}, [])

	// Don't render if no schedules and not loading
	if (!isLoading && schedules?.data?.length === 0) {
		return null
	}

	const canNavigate =
		schedules?.meta?.pagination && schedules?.meta?.pagination?.total_pages > 1

	const handleViewAllClick = () => {
		const params = new URLSearchParams()
		params.set('content_category_ids', String(section.filterValue))
		params.set('title', section.title)
		navigate(`/schedules/view/all?${params.toString()}`)
	}

	return (
		<div className="mb-8">
			{/* Section Header */}
			<div className="flex items-center justify-between md:mb-4">
				<h2 className="flex w-full items-baseline justify-between md:justify-start gap-4 m-0 text-foreground md:text-2xl text-lg md:font-normal font-medium leading-[130%]">
					{section.title}
					<button
						onClick={handleViewAllClick}
						className={
							'flex items-end bg-transparent border-none text-foreground text-xs font-medium underline decoration-muted-foreground underline-offset-2 p-0 m-0 transition-opacity duration-200 opacity-90 cursor-pointer hover:opacity-100'
						}
					>
						{t('entities.music.view_all')}
					</button>
				</h2>
				{/* Navigation buttons for pagination */}
				<div className="hidden md:flex items-center gap-1">
					<button
						onClick={() => handlePageChange('prev')}
						disabled={!canNavigate}
						className={`bg-zinc-700 border-none rounded-full w-8 h-8 flex items-center justify-center transition-opacity duration-200 ${
							canNavigate
								? 'cursor-pointer opacity-100 hover:opacity-80'
								: 'cursor-not-allowed opacity-50'
						}`}
					>
						<Icon
							name="chevron-left"
							width="16px"
							height="16px"
							color="white"
						/>
					</button>
					<button
						onClick={() => handlePageChange('next')}
						disabled={!canNavigate}
						className={`bg-zinc-700 border-none rounded-full w-8 h-8 flex items-center justify-center transition-opacity duration-200 ${
							canNavigate
								? 'cursor-pointer opacity-100 hover:opacity-80'
								: 'cursor-not-allowed opacity-50'
						}`}
					>
						<Icon
							name="chevron-right"
							width="16px"
							height="16px"
							color="white"
						/>
					</button>
				</div>
			</div>
			{isLoading ? (
				<ScheduleSkeleton count={5} />
			) : (
				<ScheduleCarousel
					ref={carouselRef}
					schedules={schedules?.data || []}
					onScheduleClick={onScheduleClick}
					onFavoriteClick={
						onFavoriteClick
							? (schedule) => onFavoriteClick(schedule, false)
							: undefined
					}
					isLoading={isLoading}
					onScrollStateChange={handleScrollStateChange}
					showActions={true}
					onAssign={onAssign}
				/>
			)}
		</div>
	)
}

export default FilteredScheduleSection
